import { useEffect, useMemo, useRef, useState } from 'react';
import { CONNECT, ONLINE, SUBCRIBE_TO_GAME_UPDATES } from 'constants/eventConstants';
import { useAppSelector } from 'hooks';
import { getCookie } from 'utils/functions';
import { isRecentlyExitedTable } from 'utils/recentlyExitedTables';

type SocketLike = {
  id?: string;
  connected?: boolean;
  emit: (event: string, payload?: unknown) => void;
  on: (event: string, handler: (data?: unknown) => void) => void;
  off: (event: string, handler?: (data?: unknown) => void) => void;
};

/**
 * Keeps every cash table the player is waitlisted on OPEN (observed) so it shows
 * up as a grid cell / table tab — matching what the player sees before a refresh
 * and what they expect right after joining a waitlist from the lobby.
 *
 * The multi-table grid renders one cell per `state.tables` entry, and a
 * `state.tables` entry only exists for a table we hold a live subscription to
 * (seated, observed, or watched). Joining a waitlist touches `state.waitlist`
 * ONLY — it never opens the table — which produced two reported bugs:
 *
 *   • Join from the lobby: the queued table never opened, so it had no cell.
 *   • Refresh: the waitlist membership rehydrated (header "WAIT" chip) but the
 *     table subscription did not (GOT_PLAYER_GAMES lists SEATED tables only, so
 *     App.tsx's auto-resub never re-subscribed the queued table), so its cell
 *     silently disappeared.
 *
 * This watcher reconciles the gap: for each of my waitlist spots that isn't
 * already in `state.tables`, it opens the table exactly the way observing does
 * — ONLINE + SubscribeToGameUpdates, mirroring GameService.joinTable. That makes
 * the engine push the table snapshot, which creates the `state.tables` entry and
 * therefore the cell. It deliberately does NOT emit REGISTER_SOCKET { page:
 * 'game' }: that is the "this is my current game" registration (last-write-wins
 * on the engine) and would hijack the single-table view's focus — the same
 * reason App.tsx's seated-table resub omits it.
 */
const useOpenWaitlistedTables = (socket: SocketLike | null | undefined) => {
  const mySpots = useAppSelector((state) => state.waitlist.mySpotByGameId);
  const tables = useAppSelector((state) => (state as any).tables) as Record<string, unknown>;
  const playerGuid = useAppSelector((state) => state.user.playerGUID);
  const playerName = useAppSelector((state) => state.user.playerName);

  // gameIds we've already emitted an open for on THIS connection, so a snapshot
  // still in flight (state.tables entry not created yet) isn't re-requested on
  // every render. Cleared on (re)connect because the API Gateway subscription is
  // per-connection — a reconnect must re-open the tables.
  const openedRef = useRef<Set<string>>(new Set());
  // Bumped on every (re)connect. It's a dep of the open effect below so a
  // reconnect actually RE-RUNS it — clearing openedRef alone wouldn't, since the
  // effect is otherwise keyed on the (unchanged) set of pending game ids.
  const [connectEpoch, setConnectEpoch] = useState(0);

  useEffect(() => {
    if (!socket) return;
    const onConnect = () => {
      openedRef.current.clear();
      setConnectEpoch((n) => n + 1);
    };
    socket.on(CONNECT, onConnect);
    return () => {
      socket.off(CONNECT, onConnect);
    };
  }, [socket]);

  // Waitlist spots whose table isn't open yet (and isn't inside the just-exited
  // grace window — don't fight an in-progress Leave Table). Keyed to a stable
  // string so this doesn't re-run on every unrelated table broadcast.
  const pending = useMemo(
    () =>
      Object.values(mySpots)
        .map((spot) => ({ gid: String(spot.gameId), game: spot.gameType || 'texas' }))
        .filter(
          ({ gid }) => gid && gid !== '0' && !tables?.[gid] && !isRecentlyExitedTable(gid)
        ),
    [mySpots, tables]
  );
  const pendingKey = pending.map((p) => `${p.gid}:${p.game}`).join(',');

  useEffect(() => {
    if (!socket || !socket.connected || !pending.length) return;
    const guid = playerGuid || localStorage.getItem('playerGUID') || '';
    if (!guid) return;
    const name = playerName || localStorage.getItem('playerName') || '';
    const deviceId = getCookie('_socketuuid');

    pending.forEach(({ gid, game }) => {
      if (openedRef.current.has(gid)) return;
      openedRef.current.add(gid);
      // seatId 0 → observer (not seated). Mirrors GameService.joinTable's ONLINE
      // payload so the engine returns this table's snapshot to our connection.
      socket.emit(ONLINE, {
        gameID: gid,
        tableID: gid,
        game,
        username: name,
        socketId: socket.id,
        data: '',
        playerGUID: guid,
        seatId: 0,
        clubId: 0,
        level: 0,
        language: 'en',
        displayName: name,
        tournamentId: '',
        uid: '',
        deviceId
      });
      socket.emit(SUBCRIBE_TO_GAME_UPDATES, {
        gameID: gid,
        game,
        playerGUID: guid,
        username: name,
        deviceId
      });
    });
    // pendingKey captures the (gameId, gameType) set; iterate the current
    // `pending` snapshot inside. connectEpoch re-runs us after a reconnect.
    // socket.id/guid/name are read at emit time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, pendingKey, connectEpoch, playerGuid, playerName]);
};

export default useOpenWaitlistedTables;
